import { CurrencyInput, Dialog } from '@cimplico/components';
import { useState } from 'react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useUpdateSubscription } from '../../../../../queries/use-update-subscription';
import { Subscription, SubscriptionStatus } from '../../../../../types/firm';
import { toastNotification } from '../../../../../components/toast/Toast';
import { formatDate } from '../../../../../utilities/formatting';

type EditEntityAllocationDialogProps = {
    subscription: Subscription;
    isOpen: boolean;
    onClose: () => void;
};

function EditEntityAllocationDialog({ subscription, isOpen, onClose }: EditEntityAllocationDialogProps): JSX.Element {
    const [quantity, setQuantity] = useState<number | undefined>(subscription.activeEntities.allocation);
    const updateSubscriptionQuery = useUpdateSubscription(subscription.firm.id);

    const disableDecrease =
        quantity === 0 ||
        (quantity || 0) <= subscription.activeEntities.count ||
        (Boolean(subscription.schedule?.duration) && (quantity || 0) <= subscription.activeEntities.allocation);

    const description =
        subscription.status === SubscriptionStatus.TRIALING
            ? `Edit your firm's active entity allocation. Increasing or decreasing the amount will occur immediately.`
            : `Edit your firm's active entity allocation. Increasing the amount will occur immediately. Decreasing the count will occur on ${formatDate(
                  subscription.annualBillingPeriodEnd
              )}.`;

    function update(): void {
        if (!quantity || quantity === subscription.activeEntities.allocation) {
            return;
        }

        updateSubscriptionQuery.mutate(
            { quantity },
            {
                onSuccess: () => {
                    toastNotification('success', 'Successfully updated active entity allocation.');
                    onClose();
                },
                onError: () => {
                    toastNotification('error', 'Unable to update active entity allocation.');
                },
            }
        );
    }

    return (
        <Dialog
            type="info"
            title="Active Entity Allocation"
            description={description}
            isOpen={isOpen}
            onClose={onClose}
            onSecondaryActionClick={onClose}
            onPrimaryActionClick={update}
            primaryAction={updateSubscriptionQuery.isLoading ? 'Updating...' : 'Update'}
            secondaryAction="Cancel"
            isLoading={updateSubscriptionQuery.isLoading}
        >
            <div className="mr-[76px]  pb-6">
                {subscription.schedule?.duration && (
                    <div className="flex items-start gap-1 mb-2">
                        <ExclamationTriangleIcon className="text-yellow-500 h-5 w-5 shrink-0" />
                        <p className="text-gray-800 text-sm leading-4">
                            Please cancel the scheduled update for the billing period before decreasing the active entity allocation.
                        </p>
                    </div>
                )}
                <div className="flex gap-2 items-center justify-center">
                    <button
                        type="button"
                        className="rounded-full border border-gray-400 text-indigo-600  h-8 w-8 flex items-center justify-center leading-4"
                        onClick={(): void => setQuantity((q) => (q || 0) - 1)}
                        disabled={disableDecrease}
                    >
                        <MinusIcon className="h-5 w-5" />
                    </button>
                    <div className="w-20">
                        <CurrencyInput
                            onChange={(e): void => setQuantity(e.target.value ? +e.target.value : undefined)}
                            value={quantity?.toString() || ''}
                            textAlign="center"
                        />
                    </div>

                    <button
                        type="button"
                        className="rounded-full border border-gray-400 text-indigo-600  h-8 w-8 flex items-center justify-center leading-4"
                        onClick={(): void => setQuantity((q) => (q || 0) + 1)}
                    >
                        <PlusIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </Dialog>
    );
}
export default EditEntityAllocationDialog;
